import { useQuery } from "@tanstack/react-query"
import { useContext, useEffect, useState, useCallback, useMemo } from "react"
import UiContext from "../../context/uiProvider"
import { DESKTOP, TABLET } from "../../utils/screenSize"
import useUser from "../../hooks/useUser"
import { Organization } from "../../Interfaces"
import { organizationService } from "../../services/organizationService"
import Table from "./common/Table"
import { useSiteTerminology } from "../../contexts/PublicSiteContext"

interface OrganizationsTableProps {
  userProfileId?: number;
}

export default function OrganizationsTable({ userProfileId = undefined }: OrganizationsTableProps) {
  const { id } = useUser()
  const { deviceView } = useContext(UiContext)
  const { managementCompanySingular, areasLabel } = useSiteTerminology()
  const userId = userProfileId || id
  const [searchTerm, setSearchTerm] = useState<string>("")

  const { data, isLoading } = useQuery<Partial<Organization[]>>(["organizations", "userId" + userId], () => organizationService.getAllByUser(userId), { cacheTime: 5000, suspense: true })

  const [filteredData, setFilteredData] = useState<Partial<Organization[]> | undefined | null>(null)

  useEffect(() => {
    if (data) {
      // Filter out undefined values and then apply search filter
      const validOrganizations = data.filter((org): org is Organization => org !== undefined)

      if (searchTerm) {
        const filtered = validOrganizations.filter(org =>
          org.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          org.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          org.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          org.state?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          org.zipcode?.toLowerCase().includes(searchTerm.toLowerCase())
        )
        setFilteredData(filtered)
      } else {
        setFilteredData(validOrganizations)
      }
    }
  }, [data, searchTerm])

  const headers = useMemo(() => {
    const cols: { label: string, key: string, to?: string }[] = [
      { label: "Id", key: "id", to: "/app/organizations" },
      { label: "Name", key: "name", to: "/app/organizations" },
    ]
    if (deviceView >= TABLET) {
      cols.push({ label: managementCompanySingular, key: "managementCompanyName" }, { label: "Locations", key: "totalLocations" })
    }
    if (deviceView >= DESKTOP) {
      cols.push({ label: "Employees", key: "totalEmployees" })
    }
    return cols
  }, [deviceView, managementCompanySingular])

  const handleSearch = useCallback((query: string) => {
    setSearchTerm(query)
  }, [])

  if (!filteredData || isLoading) return null

  return (
    <Table data={filteredData} title={areasLabel} to="/app/addOrganization" handleSearch={handleSearch} headers={headers} />
  )
}